import type { NestedFilter } from '@@partner:types:graphql'
import type { Shop } from '@@partner:types:app'

import React, { useCallback, useMemo, useState } from 'react'
import { generatePath, Link, NavLink, useParams } from 'react-router-dom'

import { useFetchAllShops } from '@@partner:graphql/query/shop/allShops.gql'

import { AddOutline } from 'react-ionicons'
import { Button, FlexboxGrid, Nav, Navbar, Pagination, SelectPicker, Tag } from 'rsuite'

import ShopsFilter from '@components/shop/table/ShopsFilter'
import ShopTopMenu from '@components/shop/ShopTopMenu'
import GraphQLCommonErrorDisplay from '@components/common/GraphQLCommonErrorDisplay'
import ResponsiveTable, { ResponsiveTableColumn } from '@components/common/ResponsiveTable/ResponsiveTable'
import CommonStatusLabel from '@components/common/CommonStatusLabel'

import { useGridHelper } from '@@partner:helpers/crud/grid'
import {
  formatDateObjectToTimeString,
  representTimeWithDateObject
} from '@@partner:helpers/date-utils'

import { ROUTE_SHOPS_CREATE_SHOP, ROUTE_SHOPS_EDIT_SHOP, ROUTE_SHOPS_SLOTS } from '@@partner:config/routes'

export default function ShopsPage () {
  const { brandSlug } = useParams<any>()

  const [ filter, setFilter ] = useState<NestedFilter>({
    fields: [ 'id' ],
    op: 'unwrap'
  })

  const {
    page,
    limit,
    defaultLimitOptions,
    tableSort,
    handlePageSelect,
    handleLimitSelect,
    handleSortColumn
  } = useGridHelper()

  const { data, loading, error } = useFetchAllShops({
    pager: {
      page,
      limit
    },
    filter,
    sort: {
      fieldName: tableSort.fieldName,
      direction: tableSort.direction
    }
  })

  const handleFilterChanged = useCallback((updatedFilter: NestedFilter) => {
    setFilter(updatedFilter)
    handlePageSelect(1)
  }, [ handlePageSelect ])

  const shops = data?.allShops?.edges ?? []
  const pageInfo = data?.allShops?.pageInfo

  // Колонки для ResponsiveTable
  const columns: ResponsiveTableColumn<Shop>[] = useMemo(() => [
    {
      dataKey: 'id',
      header: 'ИД',
      sortable: true,
      flexGrow: 1,
      isCardTitle: true
    },
    {
      dataKey: 'name',
      header: 'Название',
      sortable: true,
      flexGrow: 2
    },
    {
      dataKey: 'address',
      header: 'Адрес',
      sortable: true,
      flexGrow: 2
    },
    {
      dataKey: 'status',
      header: 'Статус',
      sortable: true,
      flexGrow: 1,
      isCardBadge: true,
      render: (shop) => <CommonStatusLabel status={shop.status} />
    },
    {
      dataKey: 'workHours',
      header: 'Рабочие часы',
      flexGrow: 1,
      hideOnMobile: true,
      render: (shop) => (
        <Tag>
          <span className="whitespace-nowrap">
            {formatDateObjectToTimeString(representTimeWithDateObject(shop.shopOpenTime))}
          </span>
          &nbsp;-&nbsp;
          <span className="whitespace-nowrap">
            {formatDateObjectToTimeString(representTimeWithDateObject(shop.shopCloseTime))}
          </span>
        </Tag>
      )
    },
    {
      dataKey: 'tzOffset',
      header: 'Время (+/-)',
      flexGrow: 1,
      hideOnMobile: true,
      render: (shop) => <Tag>{shop.tzOffset}</Tag>
    }
  ], [])

  const renderActions = useCallback((shop: Shop) => (
    <>
      <Button
        color="cyan"
        appearance="primary"
        as={Link}
        to={generatePath(ROUTE_SHOPS_EDIT_SHOP, {
          brandSlug,
          shopId: shop.id
        })}
        size="xs"
      >
        Изменить
      </Button>
      <Button
        color="green"
        appearance="primary"
        as={Link}
        to={generatePath(ROUTE_SHOPS_SLOTS, {
          brandSlug,
          type: 'shop',
          shopId: shop.id
        })}
        size="xs"
        style={{ marginLeft: 4 }}
      >
        Слоты
      </Button>
    </>
  ), [ brandSlug ])

  if (error) {
    return (
      <GraphQLCommonErrorDisplay
        title="Ошибка загрузки магазинов"
        error={error}
      />
    )
  }

  return (
    <div className="ShopsPage">
      <h1 className="u-heading">Магазины</h1>
      <ShopTopMenu />
      <Navbar appearance='subtle'>
        <Nav>
          <Nav.Item
            icon={<AddOutline />}
            as={NavLink}
            to={generatePath(ROUTE_SHOPS_CREATE_SHOP, {
              brandSlug
            })}
          >
              Добавить магазин
          </Nav.Item>
        </Nav>
      </Navbar>
      <ShopsFilter onFilterChanged={handleFilterChanged} />

      <ResponsiveTable<Shop>
        data={shops}
        columns={columns}
        loading={loading}
        rowKey="id"
        sortColumn={tableSort.fieldName}
        sortType={tableSort.sortType}
        onSortColumn={handleSortColumn}
        renderActions={renderActions}
        emptyMessage="Нет магазинов"
      />

      <FlexboxGrid
        justify="space-between"
        align="middle"
        style={{ marginTop: 10 }}
      >
        <Pagination
          last
          first
          next
          prev
          ellipsis
          activePage={page}
          pages={pageInfo?.lastPage}
          total={pageInfo?.total ?? 0}
          maxButtons={10}
          onSelect={handlePageSelect}
        />
        <SelectPicker
          value={limit}
          data={defaultLimitOptions.map((key) => ({ value: key, label: key }))}
          cleanable={false}
          searchable={false}
          onChange={handleLimitSelect}
        />
      </FlexboxGrid>
    </div>
  )
}
